/**
 * Read-only Business Relocation RFQ — buyer submitted view (design mockup).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  LuArrowLeftRight,
  LuBuilding2,
  LuCalendar,
  LuCalendarDays,
  LuCheck,
  LuCircleDollarSign,
  LuDownload,
  LuFileText,
  LuListChecks,
  LuMail,
  LuMapPin,
  LuMapPinned,
  LuPackage,
  LuPhone,
  LuScale,
  LuUser,
} from 'react-icons/lu';
import CommonService from '../../services/common.service';
import { LOGISTICS_TRANSPORT_MODES } from '../../constants/logisticsProfileFormOptions';
import { downloadBusinessRelocationRfqPdf } from '../../utils/businessRelocationRfqPdf';
import { formatBusinessRelocationFullAddress } from '../../utils/businessRelocationRfqAddressFormat';
import { getCurrentAssigneeDisplay } from '../../components/logistics/LogisticsRfqAssignedUserBadge';
import '../../assets/styles/business_relocation_rfq.scss';
import '../../assets/styles/business_relocation_rfq_submitted.scss';

function mapCountriesResponse(response) {
  const apiCountries = Array.isArray(response?.data)
    ? response.data
    : response?.data?.countries || response?.data?.master_countries || [];
  return apiCountries
    .map((country) => {
      const id = country.country_id ?? country.id ?? country.value;
      const label =
        country.country_name ?? country.country_label ?? country.label ?? country.name ?? country.country;
      return { id: id != null ? String(id) : '', label: label != null ? String(label) : '' };
    })
    .filter((c) => c.id && c.label);
}

function mapStatesResponse(response) {
  const apiStates = Array.isArray(response?.data)
    ? response.data
    : response?.data?.states || response?.data?.master_states || [];
  return apiStates
    .map((state) => {
      const id = state.state_id ?? state.id ?? state.value;
      const label = state.state_name ?? state.state_label ?? state.label ?? state.name ?? state.state;
      return { id: id != null ? String(id) : '', label: label != null ? String(label) : '' };
    })
    .filter((s) => s.id && s.label);
}

function mapCitiesResponse(response) {
  const apiCities = Array.isArray(response?.data)
    ? response.data
    : response?.data?.cities || response?.data?.master_cities || [];
  return apiCities
    .map((city) => {
      const id = city.city_id ?? city.id ?? city.value;
      const label = city.city_name ?? city.city_label ?? city.label ?? city.name ?? city.city;
      return { id: id != null ? String(id) : '', label: label != null ? String(label) : '' };
    })
    .filter((c) => c.id && c.label);
}

async function resolveLocationLabels(countryId, stateId, cityId) {
  const cid = countryId != null && String(countryId).trim() !== '' ? String(countryId).trim() : '';
  const sid = stateId != null && String(stateId).trim() !== '' ? String(stateId).trim() : '';
  const cyid = cityId != null && String(cityId).trim() !== '' ? String(cityId).trim() : '';
  if (!cid) {
    return { country: '—', state: '—', city: '—' };
  }
  try {
    const cRes = await CommonService.get('/master/company-Country');
    const countries = mapCountriesResponse(cRes);
    const countryLabel = countries.find((c) => c.id === cid)?.label || cid;

    let stateLabel = '—';
    if (sid) {
      const sRes = await CommonService.get(`/master/company-State/${cid}`);
      const states = mapStatesResponse(sRes);
      stateLabel = states.find((s) => s.id === sid)?.label || sid;
    }

    let cityLabel = '—';
    if (cyid && sid) {
      const cityRes = await CommonService.get(`/master/company-City/${sid}`);
      const cities = mapCitiesResponse(cityRes);
      cityLabel = cities.find((c) => c.id === cyid)?.label || cyid;
    }

    return { country: countryLabel, state: stateLabel, city: cityLabel };
  } catch {
    return {
      country: cid,
      state: sid || '—',
      city: cyid || '—',
    };
  }
}

const BR_SERVICE_LABELS = {
  professionalPacking: 'Professional Packing Service',
  unpackingSetup: 'Unpacking & Setup Service',
  tempStorage: 'Temporary Storage Required',
  insuranceCoverage: 'Insurance Coverage',
  customsClearance: 'Customs Clearance (International)',
  vehicleTransport: 'Vehicle Transportation',
  petRelocation: 'Pet Relocation Service',
  realTimeTracking: 'Real-time Tracking',
};

const RELOCATION_SCOPE_OPTIONS = [
  { value: 'office', label: 'Office Relocation' },
  { value: 'employee', label: 'Employee Relocation' },
  { value: 'both', label: 'Both Office & Employee' },
];

function displayValue(value) {
  if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {
    return <span className="lbrv-empty">—</span>;
  }
  return String(value);
}

function formatNumber(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US');
}

function formatTotalEstimatedValue(payload) {
  const amount = payload?.totalValueUsd;
  if (amount == null || String(amount).trim() === '') return '—';
  const currency =
    payload?.totalValueCurrency != null && String(payload.totalValueCurrency).trim() !== ''
      ? String(payload.totalValueCurrency).trim().toUpperCase()
      : 'USD';
  return `${formatNumber(amount)} ${currency}`;
}

function formatDateSlash(value) {
  if (value == null || String(value).trim() === '') return '—';
  const s = String(value).trim().slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split('-');
    return `${d}/${m}/${y}`;
  }
  return s;
}

function formatSubmittedAt(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const datePart = date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const withComma = datePart.replace(/^(\d{1,2} \w+) (\d{4})$/, '$1, $2');
  const timePart = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${withComma} at ${timePart}`;
}

function formatStatusBadge(status) {
  const s = String(status || 'pending').toLowerCase().replace(/\s+/g, '_');
  const label = String(status || 'pending')
    .replace(/_/g, ' ')
    .toUpperCase();
  return { className: `lbrv-status-badge lbrv-status-badge--${s}`, label };
}

function logisticsModeLabel(modeId) {
  if (modeId == null || String(modeId).trim() === '') return '—';
  const id = String(modeId).toLowerCase();
  const m = LOGISTICS_TRANSPORT_MODES.find((x) => x.id === id);
  return m ? m.label : String(modeId);
}

function Field({ label, value, multiline, plain, scroll }) {
  if (plain) {
    return (
      <div className="lbrq-field lbrv-field lbrv-field--plain">
        <div className="lbrq-label">{label}</div>
        <div
          className={`lbrv-plain-value${multiline ? ' lbrv-plain-value--multiline' : ''}${
            scroll ? ' lbrv-plain-value--scroll' : ''
          }`}
        >
          {displayValue(value)}
        </div>
      </div>
    );
  }
  return (
    <div className="lbrq-field lbrv-field">
      <div className="lbrq-label">{label}</div>
      <div className={`lbrv-readonly${multiline ? ' lbrv-readonly--multiline' : ''}`}>
        {displayValue(value)}
      </div>
    </div>
  );
}

function Section({ title, tone, icon, children, className = '' }) {
  return (
    <section className={`lbrq-block lbrq-block--${tone} lbrv-block ${className}`.trim()}>
      <header className="lbrq-block-head">
        <span className="lbrq-block-icon" aria-hidden>
          {icon}
        </span>
        <h2 className="lbrq-block-title">{title}</h2>
      </header>
      <div className="lbrq-block-body">{children}</div>
    </section>
  );
}

function SupplementalLocationCard({ entry, index, labelPrefix }) {
  const [loc, setLoc] = useState({ country: '…', state: '…', city: '…' });
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    (async () => {
      const labels = await resolveLocationLabels(entry.country, entry.state, entry.city);
      if (!cancelled) {
        setLoc(labels);
        setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [entry.country, entry.state, entry.city]);

  return (
    <div className="lbrv-supplemental-loc">
      <p className="lbrv-supplemental-loc__title">
        {labelPrefix} {index + 1}
      </p>
      {!ready ? (
        <p className="lbrv-muted">Loading…</p>
      ) : (
        <Field
          plain
          label="Address"
          value={formatBusinessRelocationFullAddress({
            address: entry.address,
            country: loc.country,
            state: loc.state,
            city: loc.city,
            pincode: entry.pincode,
          })}
          multiline
        />
      )}
    </div>
  );
}

function SupplementalLocationsList({ items, labelPrefix }) {
  const list = Array.isArray(items) ? items : [];
  if (!list.length) return null;
  return (
    <div className="lbrv-supplemental-locs">
      {list.map((entry, idx) => (
        <SupplementalLocationCard
          key={`${labelPrefix}-${idx}-${entry.pincode || ''}`}
          entry={entry}
          index={idx}
          labelPrefix={labelPrefix}
        />
      ))}
    </div>
  );
}

/**
 * @param {{ payload: Record<string, unknown>, embedded?: boolean, showStaffDetails?: boolean, hideSummary?: boolean, showOverviewHeading?: boolean }} props
 */
export default function BusinessRelocationRfqSubmittedView({
  payload,
  embedded = false,
  showStaffDetails = false,
  hideSummary = false,
  showOverviewHeading = false,
}) {
  const rfq = payload || {};
  const [currentLoc, setCurrentLoc] = useState({ country: '…', state: '…', city: '…' });
  const [destLoc, setDestLoc] = useState({ country: '…', state: '…', city: '…' });
  const [locReady, setLocReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLocReady(false);
    (async () => {
      const [cur, dest] = await Promise.all([
        resolveLocationLabels(rfq.currentCountry, rfq.currentState, rfq.currentCity),
        resolveLocationLabels(rfq.destCountry, rfq.destState, rfq.destCity),
      ]);
      if (cancelled) return;
      setCurrentLoc(cur);
      setDestLoc(dest);
      setLocReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [
    rfq.currentCountry,
    rfq.currentState,
    rfq.currentCity,
    rfq.destCountry,
    rfq.destState,
    rfq.destCity,
  ]);

  const additionalServices =
    rfq.additionalServices && typeof rfq.additionalServices === 'object' && !Array.isArray(rfq.additionalServices)
      ? rfq.additionalServices
      : {};

  const selectedServices = useMemo(
    () =>
      Object.entries(additionalServices)
        .filter(([, on]) => Boolean(on))
        .map(([k]) => BR_SERVICE_LABELS[k] || k),
    [additionalServices],
  );

  const scope = rfq.relocationScope != null ? String(rfq.relocationScope).toLowerCase() : '';
  const statusBadge = formatStatusBadge(rfq.status);
  const rfqRef = rfq.rfqId || rfq.rfqReference || '—';
  const buyerLabel = rfq.buyerName || rfq.contactPerson || '—';
  const submittedAt = rfq.createdAt || rfq.submittedDate;

  const relocationLabel =
    RELOCATION_SCOPE_OPTIONS.find((opt) => opt.value === scope)?.label ||
    rfq.relocationType ||
    scope ||
    '—';

  const assignee = showStaffDetails ? getCurrentAssigneeDisplay(rfq) : { name: '', role: null };

  const handleDownload = () => {
    try {
      downloadBusinessRelocationRfqPdf({
        rfq,
        currentLoc,
        destLoc,
        relocationLabel,
        selectedServices,
        formatSubmittedAt,
        formatDateSlash,
        logisticsModeLabel,
        formatNumber,
      });
    } catch (err) {
      console.error('Failed to generate RFQ PDF', err);
    }
  };

  return (
    <div className="lbrv-root">
      {!embedded ? (
        <div className="lbrv-hero-row">
          <div className="lbrv-hero-row__text">
            <h1 className="lbrv-title">Business Relocation RFQ</h1>
            <p className="lbrv-subtitle">Comprehensive office and employee relocation services</p>
          </div>
          <aside className="lbrv-status-panel" aria-label="RFQ status">
            <div className="lbrv-status-panel__seg">
              <span className="lbrv-status-panel__label">Status</span>
              <span className={statusBadge.className}>{statusBadge.label}</span>
            </div>
            <div className="lbrv-status-panel__divider" aria-hidden />
            <div className="lbrv-status-panel__seg lbrv-status-panel__seg--date">
              <span className="lbrv-status-panel__sublabel">Submitted on</span>
              <span className="lbrv-submitted-on">
                <LuCalendar aria-hidden />
                <span>{formatSubmittedAt(submittedAt)}</span>
              </span>
            </div>
          </aside>
        </div>
      ) : null}

      {!hideSummary ? (
      <div className="lbrv-summary">
        <div className="lbrv-summary__cell">
          <span className="lbrv-summary__icon" aria-hidden>
            <LuFileText />
          </span>
          <div className="lbrv-summary__text">
            <span className="lbrv-summary__label">RFQ ID</span>
            <strong className="lbrv-summary__value">{rfqRef}</strong>
          </div>
        </div>
        <div className="lbrv-summary__cell">
          <span className="lbrv-summary__icon" aria-hidden>
            <LuUser />
          </span>
          <div className="lbrv-summary__text">
            <span className="lbrv-summary__label">Buyer</span>
            <strong className="lbrv-summary__value">{buyerLabel}</strong>
          </div>
        </div>
        <div className="lbrv-summary__cell">
          <span className="lbrv-summary__icon" aria-hidden>
            <LuBuilding2 />
          </span>
          <div className="lbrv-summary__text">
            <span className="lbrv-summary__label">Company</span>
            <strong className="lbrv-summary__value">{rfq.companyName || '—'}</strong>
          </div>
        </div>
        <div className="lbrv-summary__cell">
          <span className="lbrv-summary__icon" aria-hidden>
            <LuPhone />
          </span>
          <div className="lbrv-summary__text">
            <span className="lbrv-summary__label">Contact</span>
            <strong className="lbrv-summary__value">{rfq.contactNumber || '—'}</strong>
          </div>
        </div>
        <div className="lbrv-summary__cell">
          <span className="lbrv-summary__icon" aria-hidden>
            <LuMail />
          </span>
          <div className="lbrv-summary__text">
            <span className="lbrv-summary__label">Email</span>
            <strong className="lbrv-summary__value">{rfq.companyEmail || '—'}</strong>
          </div>
        </div>
        {showStaffDetails && assignee.name ? (
          <div className="lbrv-summary__cell">
            <span className="lbrv-summary__icon" aria-hidden>
              <LuUser />
            </span>
            <div className="lbrv-summary__text">
              <span className="lbrv-summary__label">Current Assignee</span>
              <strong className="lbrv-summary__value">
                {assignee.name}
                {assignee.role ? ` (${assignee.role})` : ''}
              </strong>
            </div>
          </div>
        ) : null}
      </div>
      ) : null}

      <div className="lbrv-card-inner">
        {showOverviewHeading ? (
          <section className="lrfv-relocation-overview" aria-labelledby="lrfv-relocation-overview-title">
            <header className="lrfv-relocation-overview__head">
              <span className="lrfv-relocation-overview__icon" aria-hidden>
                <LuPackage />
              </span>
              <h2 id="lrfv-relocation-overview-title" className="lrfv-relocation-overview__title">
                Relocation Overview
              </h2>
            </header>
            <div className="lrfv-relocation-overview__body">
              <div className="lbrv-grid-row lbrv-grid-row--3">
                <Section title="Type of Relocation" tone="orange" icon={<LuArrowLeftRight />}>
                  <div className="lbrv-relocation-type" role="group" aria-label="Type of relocation">
                    {RELOCATION_SCOPE_OPTIONS.map((opt) => {
                      const active = scope === opt.value;
                      return (
                        <span
                          key={opt.value}
                          className={`lbrq-relocation-pill lbrv-relocation-pill${
                            active ? ' lbrv-relocation-pill--active' : ' lbrv-relocation-pill--inactive'
                          }`}
                        >
                          {active ? (
                            <span className="lbrv-relocation-pill__check" aria-hidden>
                              <LuCheck />
                            </span>
                          ) : null}
                          {opt.label}
                        </span>
                      );
                    })}
                  </div>
                </Section>

                <Section title="Current Location" tone="orange" icon={<LuMapPin />}>
                  {!locReady ? (
                    <p className="lbrv-muted">Loading location names…</p>
                  ) : (
                    <>
                      <Field
                        plain
                        label="Address"
                        value={formatBusinessRelocationFullAddress({
                          address: rfq.currentAddress,
                          country: currentLoc.country,
                          state: currentLoc.state,
                          city: currentLoc.city,
                          pincode: rfq.currentPincode,
                        })}
                        multiline
                      />
                      <SupplementalLocationsList
                        items={rfq.supplementalLocations?.current}
                        labelPrefix="Additional current address"
                      />
                    </>
                  )}
                </Section>

                <Section title="Destination Location" tone="orange" icon={<LuMapPinned />}>
                  {!locReady ? (
                    <p className="lbrv-muted">Loading location names…</p>
                  ) : (
                    <>
                      <Field
                        plain
                        label="Address"
                        value={formatBusinessRelocationFullAddress({
                          address: rfq.destAddress,
                          country: destLoc.country,
                          state: destLoc.state,
                          city: destLoc.city,
                          pincode: rfq.destPincode,
                        })}
                        multiline
                      />
                      <SupplementalLocationsList
                        items={rfq.supplementalLocations?.destination}
                        labelPrefix="Additional destination address"
                      />
                    </>
                  )}
                </Section>
              </div>
            </div>
          </section>
        ) : (
        <div className="lbrv-grid-row lbrv-grid-row--3">
          <Section title="Type of Relocation" tone="orange" icon={<LuArrowLeftRight />}>
            <div className="lbrv-relocation-type" role="group" aria-label="Type of relocation">
              {RELOCATION_SCOPE_OPTIONS.map((opt) => {
                const active = scope === opt.value;
                return (
                  <span
                    key={opt.value}
                    className={`lbrq-relocation-pill lbrv-relocation-pill${
                      active ? ' lbrv-relocation-pill--active' : ' lbrv-relocation-pill--inactive'
                    }`}
                  >
                    {active ? (
                      <span className="lbrv-relocation-pill__check" aria-hidden>
                        <LuCheck />
                      </span>
                    ) : null}
                    {opt.label}
                  </span>
                );
              })}
            </div>
          </Section>

          <Section title="Current Location" tone="orange" icon={<LuMapPin />}>
            {!locReady ? (
              <p className="lbrv-muted">Loading location names…</p>
            ) : (
              <>
                <Field
                  plain
                  label="Address"
                  value={formatBusinessRelocationFullAddress({
                    address: rfq.currentAddress,
                    country: currentLoc.country,
                    state: currentLoc.state,
                    city: currentLoc.city,
                    pincode: rfq.currentPincode,
                  })}
                  multiline
                />
                <SupplementalLocationsList
                  items={rfq.supplementalLocations?.current}
                  labelPrefix="Additional current address"
                />
              </>
            )}
          </Section>

          <Section title="Destination Location" tone="orange" icon={<LuMapPinned />}>
            {!locReady ? (
              <p className="lbrv-muted">Loading location names…</p>
            ) : (
              <>
                <Field
                  plain
                  label="Address"
                  value={formatBusinessRelocationFullAddress({
                    address: rfq.destAddress,
                    country: destLoc.country,
                    state: destLoc.state,
                    city: destLoc.city,
                    pincode: rfq.destPincode,
                  })}
                  multiline
                />
                <SupplementalLocationsList
                  items={rfq.supplementalLocations?.destination}
                  labelPrefix="Additional destination address"
                />
              </>
            )}
          </Section>
        </div>
        )}

        <div className="lbrv-grid-row lbrv-grid-row--2">
          <Section title="Total Shipment Summary" tone="orange" icon={<LuPackage />}>
            <div className="lbrv-stat-list">
              <div className="lbrv-stat-line">
                <span className="lbrv-stat-line__icon-wrap" aria-hidden>
                  <LuScale className="lbrv-stat-line__icon" />
                </span>
                <span className="lbrv-stat-line__label">Total Estimated Weight</span>
                <strong className="lbrv-stat-line__value">
                  {formatNumber(rfq.totalWeightKg)} {rfq.totalWeightUnit || 'KG'}
                </strong>
              </div>
              <div className="lbrv-stat-line">
                <span className="lbrv-stat-line__icon-wrap" aria-hidden>
                  <LuPackage className="lbrv-stat-line__icon" />
                </span>
                <span className="lbrv-stat-line__label">Total Number of Packages</span>
                <strong className="lbrv-stat-line__value">{formatNumber(rfq.totalPackages)}</strong>
              </div>
              <div className="lbrv-stat-line">
                <span className="lbrv-stat-line__icon-wrap" aria-hidden>
                  <LuCircleDollarSign className="lbrv-stat-line__icon" />
                </span>
                <span className="lbrv-stat-line__label">Total Estimated Value</span>
                <strong className="lbrv-stat-line__value">{formatTotalEstimatedValue(rfq)}</strong>
              </div>
            </div>
          </Section>

          <Section
            title="Timeline & Preferences"
            tone="orange"
            icon={<LuCalendarDays />}
            className="lbrv-block--timeline"
          >
            <div className="lbrq-row lbrq-row--2 lbrv-loc-row">
              <Field plain label="Preferred Move Date" value={formatDateSlash(rfq.preferredMoveDate)} />
              <Field plain label="Urgency Level" value={rfq.urgencyLevel} />
            </div>
            <div className="lbrq-row lbrq-row--2 lbrv-loc-row">
              <Field
                plain
                label="Preferred Logistics Mode"
                value={logisticsModeLabel(rfq.logisticsMode)}
              />
              <Field plain label="Budget Range (USD)" value={rfq.budgetRange} />
            </div>
            <Field
              plain
              scroll
              label="Special Requirements & Notes"
              value={rfq.specialNotes}
              multiline
            />
          </Section>
        </div>

        <Section title="Additional Services Required" tone="orange" icon={<LuListChecks />}>
          {selectedServices.length === 0 ? (
            <p className="lbrv-muted">None selected.</p>
          ) : (
            <div className="lbrv-svc-grid">
              {selectedServices.map((label) => (
                <div key={label} className="lbrv-svc-item">
                  <LuCheck className="lbrv-svc-check" aria-hidden />
                  {label}
                </div>
              ))}
            </div>
          )}
        </Section>

        <footer className="lbrv-footer">
          <p className="lbrv-footer__meta">
            RFQ ID: {rfqRef}
            <br />
            Submitted on {formatSubmittedAt(submittedAt)}
          </p>
          <button type="button" className="lbrv-download-btn" onClick={handleDownload}>
            <LuDownload aria-hidden />
            Download RFQ
          </button>
        </footer>
      </div>
    </div>
  );
}
